"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatValue = exports.NumberField = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const number_1 = require("@internationalized/number");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const react_stately_1 = require("react-stately");
const components_1 = require("../components");
const PresentationContext_1 = require("../components/PresentationContext");
const Css_1 = require("../Css");
const utils_1 = require("../utils");
const TextFieldBase_1 = require("./TextFieldBase");
function NumberField(props) {
    var _a;
    // Determine default alignment based on presentation context
    const { fieldProps } = (0, PresentationContext_1.usePresentationContext)();
    const alignment = (fieldProps === null || fieldProps === void 0 ? void 0 : fieldProps.numberAlignment) === "right" ? Css_1.Css.tr.jcfe.$ : Css_1.Css.tl.jcfs.$;
    const { disabled, required, readOnly, type, label, onBlur, onFocus, errorMsg, helperText, value, onChange, xss, displayDirection = false, numFractionDigits = type === "dollars" ? 2 : undefined, truncate = false, onEnter, numberFormatOptions, numIntegerDigits, useGrouping = true, sizeToContent = false, positiveOnly = false, ...otherProps } = props;
    const isDisabled = !!disabled;
    const isReadOnly = !!readOnly;
    const factor = type === "percent" || type === "cents" ? 100 : type === "basisPoints" ? 10000 : 1;
    const signDisplay = displayDirection ? "always" : "auto";
    const defaultFormatOptions = (0, react_1.useMemo)(() => ({
        [truncate ? "maximumFractionDigits" : "minimumFractionDigits"]: numFractionDigits,
        ...(numIntegerDigits !== undefined && { minimumIntegerDigits: numIntegerDigits }),
        useGrouping,
        signDisplay,
    }), [truncate, numIntegerDigits, useGrouping, signDisplay, numFractionDigits]);
    const { locale } = (0, react_aria_1.useLocale)();
    // If formatOptions isn't memo'd, a useEffect in useNumberStateField will cause jank,
    // see: https://github.com/adobe/react-spectrum/issues/1893.
    const formatOptions = (0, react_1.useMemo)(() => {
        if (numberFormatOptions !== undefined) {
            return numberFormatOptions;
        }
        const typeFormat = type === "percent"
            ? { style: "percent" }
            : type === "basisPoints"
                ? { style: "percent", minimumFractionDigits: 2 }
                : type === "cents"
                    ? { style: "currency", currency: "USD", minimumFractionDigits: 2 }
                    : type === "dollars"
                        ? { style: "currency", currency: "USD", minimumFractionDigits: numFractionDigits !== null && numFractionDigits !== void 0 ? numFractionDigits : 2 }
                        : type === "days"
                            ? { style: "unit", unit: "day", unitDisplay: "long", maximumFractionDigits: 0 }
                            : {};
        return { ...defaultFormatOptions, ...typeFormat };
    }, [type, numberFormatOptions, defaultFormatOptions, numFractionDigits]);
    const numberParser = (0, react_1.useMemo)(() => new number_1.NumberParser(locale, formatOptions), [locale, formatOptions]);
    const valueRef = (0, react_1.useRef)({ wip: false });
    // We can use this for both useNumberFieldState + useNumberField
    const useProps = {
        locale,
        // We want percents && cents to be integers, useNumberFieldState excepts them as decimals
        value: valueRef.current.wip ? valueRef.current.value : value === undefined ? Number.NaN : value / factor,
        // // This is called on blur with the final/committed value.
        onChange: (value) => {
            onChange(formatValue(value, factor, numFractionDigits, numIntegerDigits, positiveOnly));
        },
        onFocus: () => {
            valueRef.current = { wip: true, value: value === undefined ? Number.NaN : value / factor };
        },
        onBlur: () => {
            valueRef.current = { wip: false };
        },
        onKeyDown: (e) => {
            var _a;
            if (e.key === "Enter") {
                (0, utils_1.maybeCall)(onEnter);
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
            }
        },
        validationState: errorMsg !== undefined ? "invalid" : "valid",
        label: label,
        isDisabled,
        isReadOnly,
        formatOptions,
        ...otherProps,
    };
    const state = (0, react_stately_1.useNumberFieldState)(useProps);
    const inputRef = (0, react_1.useRef)(null);
    const { labelProps, inputProps, groupProps } = (0, react_aria_1.useNumberField)(useProps, state, inputRef);
    // Pretty janky, but if readOnly=true, then TextFieldBase doesn't create an input element,
    // but useNumberField _really_ wants the ref to be set, so give it a throw-away element.
    if (readOnly && !inputRef.current) {
        inputRef.current = document.createElement("input");
    }
    return ((0, jsx_runtime_1.jsx)(TextFieldBase_1.TextFieldBase, { xss: { ...alignment, ...xss }, groupProps: groupProps, labelProps: labelProps, label: label, required: required, inputProps: (0, react_aria_1.mergeProps)(inputProps, {
            size: sizeToContent ? String((_a = inputProps.value) !== null && _a !== void 0 ? _a : "").length || 1 : undefined,
        }), 
        // This is called on each DOM change, to push the latest value into the field
        onChange: (rawInputValue) => {
            const parsedValue = numberParser.parse(rawInputValue || "");
            onChange(formatValue(parsedValue, factor, numFractionDigits, numIntegerDigits, positiveOnly));
        }, inputRef: inputRef, onBlur: onBlur, onFocus: onFocus, errorMsg: errorMsg, helperText: helperText, tooltip: (0, components_1.resolveTooltip)(disabled, undefined, readOnly), ...otherProps }));
}
exports.NumberField = NumberField;
function formatValue(value, factor, numFractionDigits, numIntegerDigits, positiveOnly = false) {
    // We treat percents & cents as (mostly) integers, while useNumberField wants decimals, so
    // undo that via `* factor` and `Math.round`, but also keep any specifically-requested `numFractionDigits`,
    // i.e. for `type=percent value=12.34`, `value` will be `0.1234` that we want turn into `12.34`.
    const maybeAdjustForDecimals = numFractionDigits ? Math.pow(10, numFractionDigits) : 1;
    // Reverse the integer/decimal conversion
    const decimalAdjusted = Number.isNaN(value)
        ? undefined
        : Math.round((positiveOnly ? Math.abs(value) : value) * factor * maybeAdjustForDecimals) / maybeAdjustForDecimals;
    if (numIntegerDigits === undefined || decimalAdjusted === undefined) {
        return decimalAdjusted;
    }
    // If `numIntegerDigits` is defined, then we must truncate that manually, so that 1234.56 can turn into 34.56
    const fractionalValue = Math.round((decimalAdjusted % 1) * maybeAdjustForDecimals) / maybeAdjustForDecimals;
    const maybeNegate = decimalAdjusted < 0 ? -1 : 1;
    const trimmedInteger = Number(Math.trunc(Math.abs(decimalAdjusted))
        .toString()
        .slice(-1 * numIntegerDigits));
    return (trimmedInteger + fractionalValue) * maybeNegate;
}
exports.formatValue = formatValue;
